import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { allMovies, categories, subcategories } from '@/mocks/movies';
import Breadcrumb, { getCategoryName } from '@/components/feature/Breadcrumb';
import type { Movie } from '@/mocks/movies';

function MovieCard({ movie }: { movie: Movie }) {
  return (
    <Link to={`/detail/${movie.id}`} className="group block cursor-pointer">
      <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-3">
        <img
          src={movie.image}
          alt={`${movie.title} - 51影院`}
          title={`${movie.title} - 51影院免费在线观看`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute top-2 left-2">
          <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-accent-500 text-background-50">
            {movie.rating}
          </span>
        </div>
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 rounded-md text-xs bg-background-950/70 text-background-100">
            {movie.updateStatus}
          </span>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <span className="text-background-50 text-xs font-medium bg-primary-500/90 px-2 py-1 rounded-md whitespace-nowrap">立即观看</span>
        </div>
      </div>
      <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
        {movie.title}
      </h3>
      <p className="text-xs text-foreground-500 mt-1">
        {movie.subcategory} · {movie.year} · {movie.region}
      </p>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const [activeSubcategory, setActiveSubcategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('hot');

  useEffect(() => {
    setActiveSubcategory('all');
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentSlug]);

  const currentCategory = categories.find((c) => c.slug === currentSlug);
  const categoryName = currentCategory ? currentCategory.name : '全部';
  const currentSubs = currentSlug !== 'all' ? (subcategories[currentSlug] || []) : [];

  const filteredMovies = useMemo(() => {
    let list = allMovies;
    if (currentSlug !== 'all') {
      list = list.filter((m) => m.category === currentSlug);
    }
    if (activeSubcategory !== 'all') {
      list = list.filter((m) => m.subcategory === activeSubcategory);
    }
    if (sortBy === 'new') {
      list = [...list].sort((a, b) => b.year - a.year);
    } else if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else {
      list = [...list].sort((a, b) => b.viewCount - a.viewCount);
    }
    return list;
  }, [currentSlug, activeSubcategory, sortBy]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 51影院`,
    description: `51影院${categoryName}频道，聚合久久影院、悠久影院等优质资源，海量${categoryName}免费在线观看，无需注册。`,
    url: `https://www.51cinema.com/category/${currentSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '51影院',
      url: 'https://www.51cinema.com',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-20">
        <Breadcrumb items={breadcrumbItems} />

        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
            {categoryName === '全部' ? '影视资源大全' : `${categoryName}大全`}
          </h1>
          <p className="text-sm text-foreground-500">
            {categoryName === '全部'
              ? '51影院聚合久久影院、悠久影院、九九影院、55影院全类型影视资源，电视剧大全、电影大全、综艺节目一网打尽。'
              : `51影院${categoryName}频道为您精选久久影院、悠久影院等平台优质${categoryName}资源，免费在线观看，高清无广告。`}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-[200px] flex-shrink-0">
            <div className="lg:sticky lg:top-24">
              <h3 className="text-sm font-heading font-bold text-foreground-900 mb-3">资源分类</h3>
              <div className="flex lg:flex-col gap-1 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      currentSlug === cat.slug
                        ? 'bg-primary-500 text-background-50 font-medium'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {currentSubs.length > 0 && (
                <>
                  <h3 className="text-sm font-heading font-bold text-foreground-900 mt-6 mb-3">细分类型</h3>
                  <div className="flex lg:flex-col gap-1 flex-wrap">
                    <button
                      onClick={() => setActiveSubcategory('all')}
                      className={`px-3 py-1.5 rounded-lg text-xs transition-colors text-left whitespace-nowrap cursor-pointer ${
                        activeSubcategory === 'all'
                          ? 'bg-secondary-500 text-background-50 font-medium'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      全部
                    </button>
                    {currentSubs.map((sub) => (
                      <button
                        key={sub}
                        onClick={() => setActiveSubcategory(sub)}
                        className={`px-3 py-1.5 rounded-lg text-xs transition-colors text-left whitespace-nowrap cursor-pointer ${
                          activeSubcategory === sub
                            ? 'bg-secondary-500 text-background-50 font-medium'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {sub}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </aside>

          <main className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-primary-500">{filteredMovies.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {[
                  { value: 'hot', label: '最热' },
                  { value: 'new', label: '最新' },
                  { value: 'rating', label: '高分' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-background-50 text-foreground-900 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无相关内容</p>
                <p className="text-xs text-foreground-400 mt-1">请尝试切换其他分类查看</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}